import React, { useState, useEffect } from "react";
import axios from "axios";
import { CircularProgress, Box, Snackbar, Alert } from "@mui/material";
import "bootstrap/dist/css/bootstrap.min.css";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";

const Archivos = () => {
  const [preguntas, setPreguntas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [tipoPersonaFiltro, setTipoPersonaFiltro] = useState("todos");
  const [seccionFiltro, setSeccionFiltro] = useState("todos");
  const [tituloFiltro, setTituloFiltro] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePregunta, setDeletePregunta] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const createNewRow = () => ({
    id: `new-${Date.now()}`,
    titulo: "",
    descripcion: "",
    tipo_persona: "",
    obligatorio: true,
    tipo: "documento",
    activo: true,
    puntaje_maximo: 0,
    seccion: "",
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const authHeader = () => ({
    withCredentials: true,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      'Content-Type': 'application/json'
    },
  });

  const fetchPreguntas = async () => {
    try {
      const { data } = await axios.get(
        "https://apiprov.conduto.com.ec/api/documentos/preguntas-con-parametros",
        {
          params: {
            solo_activos: false
          },
          ...authHeader()
        }
      );

      const preguntasProcesadas = data.map((p, index) => ({
        ...p,
        id: p.id_pregunta || `temp-${index}`,
        activo: p.activo !== false,
        obligatorio: p.obligatorio !== false,
        puntaje_maximo: p.puntaje_maximo || 0,
        seccion: p.seccion || "",
        tipo_persona: p.tipo_persona || ""
      }));

      return preguntasProcesadas;

    } catch (error) {
      console.error("Error fetching preguntas:", error);
      throw error;
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const ps = await fetchPreguntas();
      setPreguntas(ps);
    } catch (err) {
      console.error("Error loading data:", err);
      showSnackbar('Error al cargar los datos desde el servidor', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleInputChange = (idPregunta, field, value) => {
    setPreguntas(prevPreguntas =>
      prevPreguntas.map(p =>
        (p.id_pregunta === idPregunta || p.id === idPregunta)
          ? { ...p, [field]: value }
          : p
      )
    );
  };

  const handleSave = async () => {
    setError(null);
    setGuardando(true);

    try {
      const preguntasPorTitulo = {};

      for (let i = 0; i < preguntas.length; i++) {
        const p = preguntas[i];
        const key = `${p.titulo}_${p.seccion}`;

        if (!preguntasPorTitulo[key]) {
          preguntasPorTitulo[key] = [];
        }
        preguntasPorTitulo[key].push({ ...p, index: i });
      }

      let processedCount = 0;

      for (const key in preguntasPorTitulo) {
        const grupo = preguntasPorTitulo[key];
        const primeraPregunta = grupo[0];

        if (primeraPregunta.tipo_persona === "ambos") {
          const tiposPersona = ["natural", "juridica"];

          for (const tp of tiposPersona) {
            try {
              const formData = new FormData();
              formData.append('titulo', primeraPregunta.titulo);
              formData.append('descripcion', primeraPregunta.descripcion || "");
              formData.append('tipo_persona', tp);
              formData.append('obligatorio', primeraPregunta.obligatorio ? "true" : "false");
              formData.append('tipo', "documento");
              formData.append('requiere_archivo', "false");

              if (primeraPregunta.puntaje_maximo !== undefined && primeraPregunta.puntaje_maximo !== null) {
                formData.append('puntaje_maximo', primeraPregunta.puntaje_maximo.toString());
              }

              if (primeraPregunta.seccion) {
                formData.append('seccion', primeraPregunta.seccion);
              }

              if (primeraPregunta.orden !== undefined) {
                formData.append('orden', primeraPregunta.orden.toString());
              }

              let idPreguntaFinal = null;

              for (const preguntaDelGrupo of grupo) {
                if (preguntaDelGrupo.tipo_persona === tp && preguntaDelGrupo.id_pregunta) {
                  idPreguntaFinal = preguntaDelGrupo.id_pregunta;
                  break;
                }
              }

              if (idPreguntaFinal) {
                await axios.put(
                  `https://apiprov.conduto.com.ec/api/documentos/preguntas/${idPreguntaFinal}`,
                  formData,
                  {
                    withCredentials: true,
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                      'Content-Type': 'multipart/form-data'
                    }
                  }
                );
              } else {
                const { data: created } = await axios.post(
                  "https://apiprov.conduto.com.ec/api/documentos/preguntas",
                  formData,
                  {
                    withCredentials: true,
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                      'Content-Type': 'multipart/form-data'
                    }
                  }
                );
                idPreguntaFinal = created.data[0]?.id_pregunta || created.data.id_pregunta;
              }

              if (primeraPregunta.puntaje_maximo !== undefined && primeraPregunta.puntaje_maximo !== null) {
                await axios.put(
                  `https://apiprov.conduto.com.ec/api/documentos/preguntas/${idPreguntaFinal}/parametro`,
                  {
                    puntaje_maximo: primeraPregunta.puntaje_maximo,
                    seccion: primeraPregunta.seccion
                  },
                  authHeader()
                );
              }

              processedCount++;

            } catch (error) {
              console.error(`Error procesando documento para ${tp}:`, error);
              const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                JSON.stringify(error.response?.data) ||
                error.message;
              throw new Error(`Error en documento "${primeraPregunta.titulo}" para ${tp}: ${errorMessage}`);
            }
          }
        } else {
          for (const preguntaDelGrupo of grupo) {
            try {
              const formData = new FormData();
              formData.append('titulo', preguntaDelGrupo.titulo);
              formData.append('descripcion', preguntaDelGrupo.descripcion || "");
              formData.append('tipo_persona', preguntaDelGrupo.tipo_persona);
              formData.append('obligatorio', preguntaDelGrupo.obligatorio ? "true" : "false");
              formData.append('tipo', "documento");
              formData.append('requiere_archivo', "false");

              if (preguntaDelGrupo.puntaje_maximo !== undefined && preguntaDelGrupo.puntaje_maximo !== null) {
                formData.append('puntaje_maximo', preguntaDelGrupo.puntaje_maximo.toString());
              }

              if (preguntaDelGrupo.seccion) {
                formData.append('seccion', preguntaDelGrupo.seccion);
              }

              if (preguntaDelGrupo.orden != null) {
                formData.append('orden', preguntaDelGrupo.orden.toString());
              }

              let idPreguntaFinal = preguntaDelGrupo.id_pregunta;

              if (idPreguntaFinal) {
                await axios.put(
                  `https://apiprov.conduto.com.ec/api/documentos/preguntas/${idPreguntaFinal}`,
                  formData,
                  {
                    withCredentials: true,
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                      'Content-Type': 'multipart/form-data'
                    }
                  }
                );
              } else {
                const { data: created } = await axios.post(
                  "https://apiprov.conduto.com.ec/api/documentos/preguntas",
                  formData,
                  {
                    withCredentials: true,
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("token")}`,
                      'Content-Type': 'multipart/form-data'
                    }
                  }
                );
                idPreguntaFinal = created.data[0]?.id_pregunta || created.data.id_pregunta;
              }

              if (preguntaDelGrupo.puntaje_maximo !== undefined && preguntaDelGrupo.puntaje_maximo !== null) {
                await axios.put(
                  `https://apiprov.conduto.com.ec/api/documentos/preguntas/${idPreguntaFinal}/parametro`,
                  {
                    puntaje_maximo: preguntaDelGrupo.puntaje_maximo,
                    seccion: preguntaDelGrupo.seccion
                  },
                  authHeader()
                );
              }

              processedCount++;

            } catch (error) {
              console.error(`Error procesando documento ${preguntaDelGrupo.index + 1}:`, error);
              const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                JSON.stringify(error.response?.data) ||
                error.message;
              throw new Error(`Error en documento "${preguntaDelGrupo.titulo}": ${errorMessage}`);
            }
          }
        }
      }

      await loadData();
      showSnackbar('Parámetros guardados correctamente', 'success');

    } catch (err) {
      console.error('Error al guardar:', err);
      const errorMessage = err.message || "Error al guardar los parámetros";
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setGuardando(false);
    }
  };

  const handleDeleteClick = (pregunta) => {
    if (!pregunta.id_pregunta) {
      setPreguntas(prev => prev.filter(p => p.id !== pregunta.id));
      showSnackbar('Documento eliminado', 'success');
      return;
    }
    setDeletePregunta(pregunta);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deletePregunta) return;

    setDeleteLoading(true);
    try {
      await axios.delete(
        `https://apiprov.conduto.com.ec/api/documentos/preguntas/${deletePregunta.id_pregunta}`,
        authHeader()
      );

      showSnackbar('Documento eliminado correctamente', 'success');
      await loadData();
      setShowDeleteConfirm(false);
      setDeletePregunta(null);
    } catch (error) {
      console.error('Error eliminando documento:', error);
      showSnackbar('Error al eliminar el documento', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const autoResize = (el) => {
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  const filtered = preguntas
    .filter(p => p.activo !== false)
    .filter((p) => {
      const coincideTipoDocumento = p.tipo === "documento";
      const coincideTipo = tipoPersonaFiltro === "todos" ? true :
        tipoPersonaFiltro === "ambos" ? (p.tipo_persona === "natural" || p.tipo_persona === "juridica") :
          p.tipo_persona === tipoPersonaFiltro;
      const coincideSeccion = seccionFiltro === "todos" ? true : p.seccion === seccionFiltro;
      const coincideTitulo = tituloFiltro
        ? p.titulo?.toLowerCase().includes(tituloFiltro.toLowerCase())
        : true;
      return coincideTipoDocumento && coincideTipo && coincideSeccion && coincideTitulo;
    });

  const DocumentoCard = ({ row, index }) => (
    <div className="card mb-3 shadow-sm">
      <div className="card-body">
        <div className="row g-2">
          <div className="col-12">
            <label className="form-label fw-semibold">Título *</label>
            <textarea
              className="form-control"
              rows={2}
              value={row.titulo || ""}
              onChange={(e) => handleInputChange(row.id_pregunta || row.id, "titulo", e.target.value)}
              onInput={(e) => autoResize(e.target)}
              ref={(el) => autoResize(el)}
              required
              style={{ resize: "none" }}
              placeholder="Ingrese el título del documento"
            />
          </div>

          <div className="col-12">
            <label className="form-label fw-semibold">Descripción</label>
            <textarea
              className="form-control"
              rows={2}
              value={row.descripcion || ""}
              onChange={(e) => handleInputChange(row.id_pregunta || row.id, "descripcion", e.target.value)}
              onInput={(e) => autoResize(e.target)}
              ref={(el) => autoResize(el)}
              style={{ resize: "none" }}
              placeholder="Descripción del documento (opcional)"
            />
          </div>

          <div className="col-12 col-sm-6">
            <label className="form-label fw-semibold">Tipo de Persona *</label>
            <select
              className="form-select"
              value={row.tipo_persona || ""}
              onChange={(e) => handleInputChange(row.id_pregunta || row.id, "tipo_persona", e.target.value)}
            >
              <option value="">Seleccionar…</option>
              <option value="natural">Persona Natural</option>
              <option value="juridica">Persona Jurídica</option>
              <option value="ambos">Ambos</option>
            </select>
          </div>

          <div className="col-12 col-sm-6">
            <label className="form-label fw-semibold">Obligatoriedad</label>
            <select
              className="form-select"
              value={row.obligatorio ? "obligatorio" : "opcional"}
              onChange={(e) =>
                handleInputChange(row.id_pregunta || row.id, "obligatorio", e.target.value === "obligatorio")
              }
            >
              <option value="obligatorio">Obligatorio</option>
              <option value="opcional">Opcional</option>
            </select>
          </div>

          <div className="col-12 col-sm-6">
            <label className="form-label fw-semibold">Puntaje Máximo</label>
            <input
              type="number"
              className="form-control"
              min="0"
              step="0.1"
              value={row.puntaje_maximo === null || row.puntaje_maximo === undefined ? "" : row.puntaje_maximo}
              onChange={(e) => {
                const value = e.target.value === "" ? null : parseFloat(e.target.value);
                handleInputChange(row.id_pregunta || row.id, "puntaje_maximo", value);
              }}
              placeholder="0"
            />
          </div>

          <div className="col-12 col-sm-6">
            <label className="form-label fw-semibold">Sección *</label>
            <select
              className="form-select"
              value={row.seccion || ""}
              onChange={(e) => handleInputChange(row.id_pregunta || row.id, "seccion", e.target.value)}
              required
            >
              <option value="">Seleccionar…</option>
              <option value="5_4">5_4</option>
              <option value="5_5">5_5</option>
            </select>
          </div>

          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center pt-2 border-top">
              <small className="text-muted">Orden: {index + 1}</small>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => handleDeleteClick(row)}
                title="Eliminar documento"
              >
                <i className="bi bi-trash me-1" />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) return (
    <Box display="flex" justifyContent="center" mt={4}>
      <CircularProgress />
    </Box>
  );

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4 align-items-center">
        <div className="col text-center text-md-start">
          <h2 className="h3">
            <i className="bi bi-file-earmark-text me-2"></i>
            Parámetros de Evaluación
          </h2>
          <p className="text-muted mb-0">
            En este apartado se muestra el listado de documentos que el proveedor deberá cargar como parte del proceso de calificación.
          </p>
        </div>
      </div>

      <hr />

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)} />
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold">Buscar por título:</label>
              <input
                type="text"
                className="form-control"
                placeholder="Escriba el título..."
                value={tituloFiltro}
                onChange={(e) => setTituloFiltro(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Filtrar por tipo de persona:</label>
              <select
                className="form-select"
                value={tipoPersonaFiltro}
                onChange={(e) => setTipoPersonaFiltro(e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="natural">Persona Natural</option>
                <option value="juridica">Persona Jurídica</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Filtrar por sección:</label>
              <select
                className="form-select"
                value={seccionFiltro}
                onChange={(e) => setSeccionFiltro(e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="5_4">5_4</option>
                <option value="5_5">5_5</option>
              </select>
            </div>
            <div className="col-12 col-md-2 d-flex align-items-end">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setTituloFiltro("");
                  setTipoPersonaFiltro("todos");
                  setSeccionFiltro("todos");
                }}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="d-none d-lg-block">
        <div className="table-responsive">
          <table className="table table-bordered table-hover">
            <thead className="table-light">
              <tr>
                <th style={{ width: 50 }}>N°</th>
                <th style={{ width: "20%" }}>Título *</th>
                <th style={{ width: "20%" }}>Descripción</th>
                <th style={{ width: "12%" }}>Tipo de Persona *</th>
                <th style={{ width: "10%" }}>Obligatoriedad</th>
                <th style={{ width: "10%" }}>Puntaje Máximo</th>
                <th style={{ width: "10%" }}>Sección *</th>
                <th style={{ width: 100 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, idx) => (
                <tr key={row.id_pregunta || row.id}>
                  <td className="fw-medium">{idx + 1}</td>
                  <td>
                    <textarea
                      className="form-control"
                      rows={1}
                      value={row.titulo || ""}
                      onChange={(e) => handleInputChange(row.id_pregunta || row.id, "titulo", e.target.value)}
                      onInput={(e) => autoResize(e.target)}
                      ref={(el) => autoResize(el)}
                      required
                      style={{ resize: "none", overflow: "hidden" }}
                    />
                  </td>
                  <td>
                    <textarea
                      className="form-control"
                      rows={1}
                      value={row.descripcion || ""}
                      onChange={(e) => handleInputChange(row.id_pregunta || row.id, "descripcion", e.target.value)}
                      onInput={(e) => autoResize(e.target)}
                      ref={(el) => autoResize(el)}
                    />
                  </td>
                  <td>
                    <select
                      className="form-select"
                      value={row.tipo_persona || ""}
                      onChange={(e) => handleInputChange(row.id_pregunta || row.id, "tipo_persona", e.target.value)}
                    >
                      <option value="">Seleccionar…</option>
                      <option value="natural">Persona Natural</option>
                      <option value="juridica">Persona Jurídica</option>
                      <option value="ambos">Ambos</option>
                    </select>
                  </td>
                  <td>
                    <select
                      className="form-select"
                      value={row.obligatorio ? "obligatorio" : "opcional"}
                      onChange={(e) =>
                        handleInputChange(row.id_pregunta || row.id, "obligatorio", e.target.value === "obligatorio")
                      }
                    >
                      <option value="obligatorio">Obligatorio</option>
                      <option value="opcional">Opcional</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      step="0.1"
                      value={row.puntaje_maximo === null || row.puntaje_maximo === undefined ? "" : row.puntaje_maximo}
                      onChange={(e) => {
                        const value = e.target.value === "" ? null : parseFloat(e.target.value);
                        handleInputChange(row.id_pregunta || row.id, "puntaje_maximo", value);
                      }}
                    />
                  </td>
                  <td>
                    <select
                      className="form-select"
                      value={row.seccion || ""}
                      onChange={(e) => handleInputChange(row.id_pregunta || row.id, "seccion", e.target.value)}
                      required
                    >
                      <option value="">Seleccionar…</option>
                      <option value="5_4">5_4</option>
                      <option value="5_5">5_5</option>
                    </select>
                  </td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteClick(row)}
                      title="Eliminar documento"
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="d-block d-lg-none">
        {filtered.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-inbox display-4 d-block mb-2"></i>
            No hay documentos que coincidan con los filtros
          </div>
        ) : (
          <div>
            {filtered.map((row, index) => (
              <DocumentoCard key={row.id_pregunta || row.id} row={row} index={index} />
            ))}
          </div>
        )}
      </div>

      <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mt-4">
        <button
          className="btn btn-success order-2 order-md-1"
          onClick={() => setPreguntas([...preguntas, createNewRow()])}
        >
          <i className="bi bi-plus-lg me-2" />Agregar Parámetro
        </button>

        <button
          className="btn btn-primary order-1 order-md-2"
          onClick={handleSave}
          disabled={guardando || filtered.some((p) => !p.titulo || (!p.tipo_persona && p.tipo_persona !== "ambos") || !p.seccion)}
        >
          {guardando ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Guardando…
            </>
          ) : (
            <>
              <i className="bi bi-save me-2" />Guardar Cambios
            </>
          )}
        </button>
      </div>

      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => {
          setShowDeleteConfirm(false);
          setDeletePregunta(null);
        }}
        onConfirm={confirmDelete}
        title={`¿Está seguro que desea eliminar el documento "${deletePregunta?.titulo || ''}"?`}
        message="Esta acción eliminará el documento de forma permanente. No se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmVariant="danger"
        loading={deleteLoading}
        icon={<i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default Archivos;